<template>
  <div class="tabs">
    <div class="tabs-header">
      <div class="tabs-nav">
        <div 
          v-for="(tab, index) in tabs" 
          :key="tab.tab" 
          :class="['tab-item', { active: activeTab === tab.tab }]"
          @click="selectTab(tab.tab, index)"
          ref="tabItems"
        >
          {{ tab.title }}
        </div>
        <div class="tab-indicator" :style="indicatorStyle"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, nextTick, onMounted, watch } from 'vue'

const props = defineProps({
  tabs: {
    type: Array,
    required: true
  },
  activeTab: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['onChoseTab'])

const tabItems = ref([])
const indicatorStyle = ref({})

const selectTab = (tab, index) => {
  emit('onChoseTab', tab)
  updateIndicator(index)
}

const updateIndicator = (activeIndex) => {
  nextTick(() => {
    if (tabItems.value && tabItems.value[activeIndex]) {
      const activeTab = tabItems.value[activeIndex]
      const { offsetLeft, offsetWidth } = activeTab
      
      indicatorStyle.value = {
        transform: `translateX(${offsetLeft}px)`,
        width: `${offsetWidth}px`
      }
    }
  })
}

const findActiveIndex = () => {
  return props.tabs.findIndex(tab => tab.tab === props.activeTab)
}

onMounted(() => {
  const activeIndex = findActiveIndex()
  if (activeIndex !== -1) {
    updateIndicator(activeIndex)
  }
})

watch(() => props.activeTab, () => {
  const activeIndex = findActiveIndex()
  if (activeIndex !== -1) {
    updateIndicator(activeIndex)
  }
})
</script>

<style scoped>
.tabs {
  display: flex;
  flex-direction: column;
}

.tabs-header {
  position: relative;
  background: #ffffff;
}

.tabs-nav {
  display: flex;
  position: relative;
  gap: 0;
}

.tab-item {
  position: relative;
  padding: 16px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  user-select: none;
}

.tab-item:hover {
  color: #374151;
  background: rgba(87, 74, 234, 0.05);
}

.tab-item.active {
  color: #574AEA;
  background: rgba(87, 74, 234, 0.08);
}

.tab-indicator {
  position: absolute;
  bottom: -1px;
  height: 3px;
  background: linear-gradient(90deg, #574AEA 0%, #6366f1 100%);
  border-radius: 2px 2px 0 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(87, 74, 234, 0.3);
}

.tabs-content {
  padding: 0;
  min-height: 400px;
}

/* Анимации для контента */
.tab-content-enter-active,
.tab-content-leave-active {
  transition: all 0.3s ease;
}

.tab-content-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.tab-content-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.tab-content-enter-to,
.tab-content-leave-from {
  opacity: 1;
  transform: translateX(0);
}

/* Адаптивность */
@media (max-width: 768px) {
  .tabs {
    margin: 0;
    padding: 0;
  }
  
  .tabs-header {
    padding: 0 12px;
    margin-bottom: 16px;
    background: white;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .tabs-nav {
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    gap: 8px;
    padding-bottom: 2px;
  }
  
  .tabs-nav::-webkit-scrollbar {
    display: none;
  }
  
  .tab-item {
    padding: 12px 16px;
    font-size: 0.875rem;
    min-width: fit-content;
    border-radius: 8px 8px 0 0;
    border-bottom: none;
  }
  
  .tab-item:hover {
    background: rgba(87, 74, 234, 0.08);
  }
  
  .tab-item.active {
    background: rgba(87, 74, 234, 0.12);
    color: #574AEA;
  }
  
  .tab-indicator {
    height: 2px;
    bottom: 0;
    border-radius: 1px 1px 0 0;
  }
}

@media (max-width: 480px) {
  .tabs-header {
    padding: 0 8px;
    margin-bottom: 12px;
  }
  
  .tabs-nav {
    gap: 4px;
  }
  
  .tab-item {
    padding: 10px 12px;
    font-size: 0.8rem;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .tab-indicator {
    height: 2px;
  }
}
</style>
